import { FiltersToolbar } from "@components/commerce/product-listing/filters/toolbar/FiltersToolbar";
import { ProductHitsWrapper } from "@components/commerce/product-listing/product-tile-list/ProductHitsWrapper";
import SortBy from "@components/commerce/product-listing/filters/toolbar/item/sortBy/SortBy";
import ProductListFooter from "@components/commerce/product/list/product-list-footer/ProductListFooter";
import { EccoLoading } from "@components/ui/ecco-loading/EccoLoading";
import { useNormalizedInfiniteHits } from "@hooks/useNormalizedInfiniteHits";
import { AnalyticsListTypes } from "@lib/analytics/analyticsTypes";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { sizes } from "@ecco/ui/tokens";
import { type FC } from "react";
import { useInstantSearch } from "react-instantsearch";
import SearchCMS from "./cms-search";
import { EmptyResultSearchPage } from "./EmptyResultSearchPage";
import { Heading } from "@ecco/ui/heading";
import { useFilterStore } from "@store/filterStore";
import { useLgBreakpoint } from "@ecco/ui/use-breakpoint";
import { useStore } from "@store/store";
import { ProductCategoryFilter } from "@components/commerce/product-listing/filters/product-category-filter/ProductCategoryFilter";
import { useShowTargetGroupFilter } from "@lib/utils/plp/useShowTargetGroupFilter";
import type { RecommendationResult } from "@lib/algolia/types";
import type { ILinksCollection } from "@contentful-api/types/contentful";

interface SearchResultsPLPProps {
    listOfFilters: any;
    routerQuery: any;
    locale: string;
    trendingRecommendations: RecommendationResult[];
    customLinksCarousel?: ILinksCollection[];
}

export const SearchResultsPLP: FC<SearchResultsPLPProps> = ({
    listOfFilters,
    routerQuery,
    locale,
    trendingRecommendations,
    customLinksCarousel,
}) => {
    const { get: getMicrocopy } = useMicrocopy();
    const filtersDialogOpened = useFilterStore((state) => state.filtersDialogOpened);
    const { hits, resultQuery, numberOfHits } = useNormalizedInfiniteHits();
    const { status } = useInstantSearch();
    const isDesktopView = useLgBreakpoint();
    const hasActiveTimer = useStore((state) => state.data.hasActiveTimer);

    const routerQueryValue =
        typeof routerQuery === "string"
            ? routerQuery
            : typeof routerQuery === "object" && routerQuery && "query" in routerQuery
              ? String(routerQuery.query ?? "")
              : "";
    const query = resultQuery || routerQueryValue || "";
    const isAlgoliaLoading = status === "loading" || status === "stalled";
    const shouldRenderProductCategoryFilter = useShowTargetGroupFilter(listOfFilters);

    return (
        <>
            {numberOfHits !== 0 && (
                <Heading as="h1" color="gray.500" mt={[4, 6]} mb={[10, 12]} ml={[3, 3, 4, 6]}>
                    <span>
                        {getMicrocopy(CommonCMS.page, SearchCMS.searchInputWithQueryHeadline)}
                    </span>
                    <span
                        key={query}
                        style={{ color: "black", display: "inline", margin: "0 4px" }}
                    >
                        {`“${query}”`}
                    </span>
                    <span
                        style={{
                            color: "black",
                            fontWeight: "bold",
                            fontSize: "var(--ec-fontSizes-mobileH3)",
                        }}
                    >
                        ({numberOfHits})
                    </span>
                </Heading>
            )}
            {shouldRenderProductCategoryFilter && <ProductCategoryFilter />}
            <Box data-testid="SearchResultsData" position="relative">
                {/* SortBy - hidden on mobile, visible on desktop, non-sticky */}
                {numberOfHits !== 0 && isDesktopView && (
                    <Box
                        position="absolute"
                        top={0}
                        mt={hasActiveTimer ? 7 : 1}
                        right={0}
                        w="auto"
                        px={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
                        zIndex={filtersDialogOpened ? 4 : 3}
                    >
                        <Flex justifyContent="flex-end" alignItems="center" height="60px">
                            <SortBy locale={locale} />
                        </Flex>
                    </Box>
                )}
                <main>
                    <Box
                        id="filters-toolbar-container"
                        position="sticky"
                        w="100%"
                        top={[
                            `calc(${sizes.navBarCollapsedHeight} + 25px)`,
                            `calc(${sizes.navBarCollapsedHeight} + 25px)`,
                            `calc(${sizes.navBarCollapsedHeight} + 35px)`,
                        ]}
                        zIndex={filtersDialogOpened ? 3 : 2}
                        boxSizing="border-box"
                        display={numberOfHits === 0 ? "none" : "block"}
                        px={["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop"]}
                    >
                        <Flex alignItems="center">
                            <Box flex={1}>
                                <FiltersToolbar listOfFilters={listOfFilters} locale={locale} />
                            </Box>
                        </Flex>
                    </Box>
                    {query === undefined || (isAlgoliaLoading && hits.length === 0) ? (
                        <EccoLoading />
                    ) : (
                        <ProductHitsWrapper
                            productListHits={hits}
                            footerContent={<ProductListFooter />}
                            noResultsContent={
                                <EmptyResultSearchPage
                                    query={query as string}
                                    trendingRecommendations={trendingRecommendations}
                                    customLinksCarousel={customLinksCarousel}
                                />
                            }
                            productListName={AnalyticsListTypes.search}
                        />
                    )}
                </main>
            </Box>
        </>
    );
};
